import React, { useEffect, useState, useRef, Suspense, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, onSnapshot, setDoc, query, where, documentId, orderBy, limit, serverTimestamp, arrayUnion, arrayRemove, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { 
  getHighestRatedShows, 
  addToWatchlist, 
  markShowAsWatching,
  markShowAsSeen,
  archiveShow,
  getUserLists,
  getShowRatings,
  getUserRating,
  addRating,
  updateRating,
  getUserLastActivity,
  getAllUsers,
  getLatestActivities,
  getLastViewedTimestamps,
  addActivity
} from '../../services/databaseService';
import { useAuth } from '../../context/AuthContext';
import './Popular.css';
import { StrawberryIcon } from '../../components/icons/StrawberryIcon';
import { Spinner } from '../../components/Spinner/Spinner';
import { ShowCard } from '../../components/ShowCard';
import { StreamingSourceModal } from '../../components/StreamingSourceModal/StreamingSourceModal';
import { useInView } from 'react-intersection-observer';
import { Toast } from '../../components/Toast/Toast';
import { getStreamingSources, getStreamingSourcesForShows } from '../../services/watchmodeApi';
import { RatingStrawberries } from '../../components/RatingStrawberries';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import RatingsOverviewModal from '../../components/RatingsOverviewModal/RatingsOverviewModal';
import { RatingModal } from '../../components/RatingModal';
import { RatingOnlyModal } from '../../components/RatingOnlyModal';
import { pageStateStore } from '../../services/pageStateStore';
import { ShowCardSkeleton } from '../../components/ShowCardSkeleton';
import { LoadingLayout } from '../../components/LoadingLayout';
import { createPortal } from 'react-dom';
import UserAvatar from '../../components/UserAvatar/UserAvatar';

const OMDB_API_KEY = '1feaa769'; // Consider moving this to environment variables

async function getIMDbRating(imdbId) {
  try {
    const response = await fetch(`https://www.omdbapi.com/?i=${imdbId}&apikey=${OMDB_API_KEY}`);
    const data = await response.json();
    return data.imdbRating;
  } catch (error) {
    console.error('Error fetching IMDb rating:', error);
    return null;
  }
}

const SHOW_STATUSES = {
  NONE: 'none',
  WATCHING: 'watching',
  WATCHLIST: 'watchlist',
  SEEN: 'seen',
  ARCHIVED: 'archived'
};

const StatusMenu = ({ show, position, onClose, currentStatus, onStatusChange }) => {
  // Create a ref for the portal container
  const portalRef = useRef(null);
  
  // Create the portal container on mount
  useEffect(() => {
    // Create a div for the portal
    const portalContainer = document.createElement('div');
    portalContainer.className = 'status-menu-portal';
    document.body.appendChild(portalContainer);
    
    // Store the reference
    portalRef.current = portalContainer;
    
    // Clean up on unmount
    return () => {
      if (portalContainer && document.body.contains(portalContainer)) {
        document.body.removeChild(portalContainer);
      }
    };
  }, []);
  
  // Don't render until we have a portal container
  if (!portalRef.current) return null;
  
  return createPortal(
    <>
      <div className="popular-status-menu-overlay" onClick={onClose} />
      <div 
        className="popular-status-menu" 
        style={{ 
          position: 'fixed',
          left: `${position.left}px`,
          top: `${position.top}px`
        }}
      >
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.WATCHING ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.WATCHING)}
        >
          <i className="fas fa-satellite-dish"></i>
          <span>Tittar på</span>
        </button>
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.WATCHLIST ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.WATCHLIST)}
        >
          <i className="fas fa-check"></i>
          <span>Ska se</span>
        </button>
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.SEEN ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.SEEN)}
        >
          <i className="fas fa-check-double"></i>
          <span>Har sett</span>
        </button>
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.ARCHIVED ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.ARCHIVED)}
        >
          <i className="fas fa-trash"></i>
          <span>Arkivera</span>
        </button>
      </div>
    </>,
    portalRef.current
  );
};

const CustomSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);

  // Ensure value is always a valid string
  const safeValue = value || 'all';

  const options = [
    { value: 'all', label: 'Alla' },
    { value: 'movie', label: 'Filmer' },
    { value: 'series', label: 'Serier' }
  ];

  // Create the portal container on mount
  useEffect(() => {
    // Create a div for the portal
    const portalContainer = document.createElement('div');
    portalContainer.className = 'filter-portal';
    document.body.appendChild(portalContainer);
    
    // Store the reference
    portalRef.current = portalContainer;
    
    // Clean up on unmount
    return () => {
      if (portalContainer && document.body.contains(portalContainer)) {
        document.body.removeChild(portalContainer);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Get the selected option label safely
  const selectedOption = options.find(opt => opt.value === safeValue) || options[0];

  return (
    <>
      <div className="friend-profile__filter-container">
        <button 
          className="friend-profile__filter-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selectedOption.label}
        </button>
      </div>

      {isOpen && portalRef.current && createPortal(
        <>
          <div className="friend-profile__filter-overlay" onClick={() => setIsOpen(false)} />
          <div className="friend-profile__filter-drawer" ref={dropdownRef}>
            <div className="friend-profile__filter-drawer-header">
              <div className="friend-profile__filter-drawer-handle" />
              <h3>Filtrera</h3>
            </div>
            <div className="friend-profile__filter-drawer-content">
              {options.map((option) => (
                <button
                  key={option.value}
                  className={`friend-profile__filter-option ${safeValue === option.value ? 'active' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </>,
        portalRef.current
      )}
    </>
  );
};

function Popular() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Initialize state with cached data more efficiently
  const getCachedShows = () => {
    try {
      const cached = sessionStorage.getItem('popularShows');
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        // Ensure we're working with serializable data
        if (parsed && parsed.shows && Array.isArray(parsed.shows) && parsed.timestamp && 
            now - parsed.timestamp < 5 * 60 * 1000) { // 5 minutes cache
          return {
            shows: parsed.shows,
            timestamp: parsed.timestamp,
            isFresh: true
          };
        }
      }
    } catch (error) {
      console.warn('Error reading cached shows:', error);
    }
    return {
      shows: [],
      timestamp: null,
      isFresh: false
    };
  };

  // Initialize users from cache
  const getCachedUsers = () => {
    try {
      const cached = sessionStorage.getItem('allUsers');
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        if (parsed && parsed.data && Array.isArray(parsed.data) && parsed.timestamp && 
            now - parsed.timestamp < 5 * 60 * 1000) { // 5 minutes cache
          return parsed.data;
        }
      }
    } catch (error) {
      console.error('Error reading cached users:', error);
    }
    return [];
  };

  // Get cached data at initialization time
  const cachedShowsData = getCachedShows();
  const cachedUsersData = getCachedUsers();

  // Move all useState declarations together
  const [shows, setShows] = useState(cachedShowsData.shows || []);
  const [loading, setLoading] = useState(!cachedShowsData.isFresh);
  const [initialLoad, setInitialLoad] = useState(!cachedShowsData.isFresh);
  const [lastFetchTime, setLastFetchTime] = useState(cachedShowsData.timestamp || null);
  const [isFreshData, setIsFreshData] = useState(!!cachedShowsData.isFresh);
  const [selectedShow, setSelectedShow] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showRatingOnlyModal, setShowRatingOnlyModal] = useState(false);
  const [selectedShowForRating, setSelectedShowForRating] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showStreamingModal, setShowStreamingModal] = useState(false);
  const [selectedShowForSources, setSelectedShowForSources] = useState(null);
  const [showRatingsOverview, setShowRatingsOverview] = useState(false);
  const [toast, setToast] = useState(null);
  const [streamingSources, setStreamingSources] = useState({});
  const [loadingStreamingSources, setLoadingStreamingSources] = useState(false);
  const [showRatings, setShowRatings] = useState([]);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [userLists, setUserLists] = useState({ watchlist: [], seenList: [], watchingList: [], archivedList: [] });
  const [listsLoading, setListsLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [filter, setFilter] = useState(() => {
    return pageStateStore.getState('popular')?.filter || 'all';
  });
  const [scrollPosition, setScrollPosition] = useState(() => {
    return pageStateStore.getState('popular')?.scrollPosition || 0;
  });
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedShowForStatus, setSelectedShowForStatus] = useState(null);
  const [currentShowStatus, setCurrentShowStatus] = useState('none');
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [allUsers, setAllUsers] = useState(cachedUsersData || []);
  const [loadingUsers, setLoadingUsers] = useState(!(cachedUsersData && cachedUsersData.length > 0));
  const [userActivities, setUserActivities] = useState({});
  const [lastViewedTimestamps, setLastViewedTimestamps] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false
  });
  const [lastDoc, setLastDoc] = useState(null);
  // Add a state for filtered shows instead of using useMemo
  const [filteredShows, setFilteredShows] = useState([]);

  // Keep refs together
  const initialMount = useRef(true);

  // Add ref for cleanup
  const unsubscribeRef = useRef(null);
  
  // Add ref to track if component is mounted
  const mounted = useRef(true);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mounted.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Modify the scroll effect to prevent the loop
  useEffect(() => {
    const handleScroll = () => {
      // Only store in pageState, don't update state on every scroll
      const position = window.scrollY;
      pageStateStore.setState('popular', { 
        scrollPosition: position,
        filter 
      });
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Initial scroll restore
    const savedPosition = pageStateStore.getState('popular')?.scrollPosition || 0;
    if (savedPosition > 0) {
      window.scrollTo(0, savedPosition);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [filter]); // Only depend on filter changes

  // Add this effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  console.log('Popular render:', { 
    loading, 
    isFreshData, 
    hasCache: !!shows, 
    lastFetch: lastFetchTime ? new Date(lastFetchTime).toISOString() : null,
    showsLength: shows.length,
    initialLoad
  });

  // Add effect for initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!mounted.current) return;

      try {
        // If we have fresh cached data, use it
        if (isFreshData && cachedShowsData.shows.length > 0) {
          setShows(cachedShowsData.shows);
          setLoading(false);
          setInitialLoad(false);
          return;
        }

        // Otherwise, fetch new data
        setLoading(true);
        const showsData = await getHighestRatedShows(20, filter === 'all' ? null : filter);
        
        if (!mounted.current) return;

        if (showsData && Array.isArray(showsData.shows)) {
          const cleanedShows = showsData.shows.map(show => ({
            id: String(show.id),
            title: String(show.title || ''),
            year: show.year ? String(show.year) : '', // Keep year as string to preserve ranges
            type: String(show.type || ''),
            poster: String(show.poster || ''),
            backdrop: String(show.backdrop || ''),
            averageRating: Number(show.averageRating) || 0,
            ratingCount: Number(show.ratingCount) || 0,
            imdb_rating: Number(show.imdb_rating) || 0,
            plot: String(show.plot || ''),
            streamingSources: Array.isArray(show.streamingSources) ? [...show.streamingSources] : []
          }));

          setShows(cleanedShows);
          setHasMore(showsData.hasMore !== false);
          setLastDoc(showsData.lastDoc);
          
          // Cache the cleaned data
          const cacheData = {
            shows: cleanedShows,
            timestamp: Date.now()
          };
          sessionStorage.setItem('popularShows', JSON.stringify(cacheData));
        }
      } catch (error) {
        console.error('Error fetching initial shows:', error);
      } finally {
        if (mounted.current) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };

    fetchInitialData();
  }, [isFreshData, filter]); // Add filter to dependencies to reload when filter changes

  // Add effect for user lists
  useEffect(() => {
    const fetchUserLists = async () => {
      if (!user || !mounted.current) return;

      try {
        setListsLoading(true);
        const lists = await getUserLists(user.uid);
        
        if (!mounted.current) return;
        
        // Clean the lists data
        const cleanedLists = {
          watchlist: (lists.watchlist || []).map(show => ({
            id: String(show.id),
            title: String(show.title || ''),
            year: Number(show.year) || 0,
            type: String(show.type || ''),
            poster: String(show.poster || ''),
            backdrop: String(show.backdrop || '')
          })),
          seenList: (lists.seenList || []).map(show => ({
            id: String(show.id),
            title: String(show.title || ''),
            year: Number(show.year) || 0,
            type: String(show.type || ''),
            poster: String(show.poster || ''),
            backdrop: String(show.backdrop || '')
          })),
          watchingList: (lists.watchingList || []).map(show => ({
            id: String(show.id),
            title: String(show.title || ''),
            year: Number(show.year) || 0,
            type: String(show.type || ''),
            poster: String(show.poster || ''),
            backdrop: String(show.backdrop || '')
          })),
          archivedList: (lists.archivedList || []).map(show => ({
            id: String(show.id),
            title: String(show.title || ''),
            year: Number(show.year) || 0,
            type: String(show.type || ''),
            poster: String(show.poster || ''),
            backdrop: String(show.backdrop || '')
          }))
        };

        setUserLists(cleanedLists);
      } catch (error) {
        console.error('Error fetching user lists:', error);
      } finally {
        if (mounted.current) {
          setListsLoading(false);
        }
      }
    };

    fetchUserLists();
  }, [user]);

  // Add effect for users data
  useEffect(() => {
    const fetchUsers = async () => {
      if (!mounted.current) return;

      try {
        setLoadingUsers(true);
        
        // If we have fresh cached users, use them
        // Use a more reliable check to avoid repetitive requests
        if (cachedUsersData && Array.isArray(cachedUsersData) && cachedUsersData.length > 0) {
          console.log('Using cached users data, length:', cachedUsersData.length);
          setAllUsers(cachedUsersData);
          setLoadingUsers(false);
          return;
        }

        console.log('Fetching fresh users data');
        // Otherwise, fetch new data
        const [usersData, activitiesData, viewedData] = await Promise.all([
          getAllUsers(),
          getLatestActivities(),
          user?.uid ? getLastViewedTimestamps(user.uid) : Promise.resolve({})
        ]);

        if (!mounted.current) return;

        // Clean and sort users
        const sortedUsers = usersData
          .filter(u => u && u.uid)
          .map(u => ({
            uid: String(u.uid),
            displayName: String(u.displayName || ''),
            firstName: String(u.firstName || ''),
            lastName: String(u.lastName || ''),
            photoURL: String(u.photoURL || ''),
            email: String(u.email || ''),
            lastActivity: u.lastActivity || 0 // Ensure we capture lastActivity
          }))
          .sort((a, b) => {
            // Current user always first
            if (a.uid === user?.uid) return -1;
            if (b.uid === user?.uid) return 1;
            
            // Then sort by most recent activity (larger timestamp = more recent)
            const aLastActivity = activitiesData?.[a.uid]?.[0]?.timestamp || a.lastActivity || 0;
            const bLastActivity = activitiesData?.[b.uid]?.[0]?.timestamp || b.lastActivity || 0;
            
            return bLastActivity - aLastActivity; // Descending order (newest first)
          });

        // Cache the sorted users
        const cacheData = {
          data: sortedUsers,
          timestamp: Date.now()
        };
        sessionStorage.setItem('allUsers', JSON.stringify(cacheData));

        setAllUsers(sortedUsers);
        setUserActivities(activitiesData || {});
        setLastViewedTimestamps(viewedData || {});
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        if (mounted.current) {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();
  }, [user?.uid]); // Remove cachedUsersData from dependencies to avoid loops

  // Add effect to load more data when user scrolls to bottom
  useEffect(() => {
    const loadMoreData = async () => {
      // Only load more if inView is true, we have more data, and we're not already loading
      if (inView && hasMore && !loadingMore && !loading) {
        setLoadingMore(true);
        console.log('Loading more shows, page:', page + 1);

        try {
          const nextPage = page + 1;
          const showsData = await getHighestRatedShows(20, filter === 'all' ? null : filter, lastDoc);
          
          if (!mounted.current) return;

          if (showsData && Array.isArray(showsData.shows)) {
            const cleanedNewShows = showsData.shows.map(show => ({
              id: String(show.id),
              title: String(show.title || ''),
              year: show.year ? String(show.year) : '', // Keep year as string to preserve ranges
              type: String(show.type || ''),
              poster: String(show.poster || ''),
              backdrop: String(show.backdrop || ''),
              averageRating: Number(show.averageRating) || 0,
              ratingCount: Number(show.ratingCount) || 0,
              imdb_rating: Number(show.imdb_rating) || 0,
              plot: String(show.plot || ''),
              streamingSources: Array.isArray(show.streamingSources) ? [...show.streamingSources] : []
            }));

            setShows(prevShows => [...prevShows, ...cleanedNewShows]);
            setPage(nextPage);
            setHasMore(showsData.hasMore !== false);
            if (showsData.lastDoc) {
              setLastDoc(showsData.lastDoc);
            }
          } else {
            setHasMore(false);
          }
        } catch (error) {
          console.error('Error loading more shows:', error);
          setToast({ message: 'Kunde inte ladda fler titlar', type: 'error' });
        } finally {
          if (mounted.current) {
            setLoadingMore(false);
          }
        }
      }
    };

    loadMoreData();
  }, [inView, hasMore, loadingMore, loading, page, lastDoc, filter]);

  // Update filtered shows whenever shows or filter changes
  useEffect(() => {
    if (!shows || !Array.isArray(shows)) {
      setFilteredShows([]);
      return;
    }
    
    if (filter === 'all') {
      setFilteredShows([...shows]);
      return;
    }
    
    const filtered = shows.filter(show => {
      if (!show) return false;
      const showType = show.type?.toLowerCase() || '';
      if (filter === 'movie') {
        return showType === 'movie' || showType === 'film';
      } else if (filter === 'series') {
        return showType === 'series' || showType === 'tv_series';
      }
      return true;
    });
    
    setFilteredShows(filtered);
  }, [shows, filter]);

  // Only show loading state on initial load
  if ((!shows.length && initialLoad) || authLoading) {
    return (
      <div className="popular-container">
        <h1>Smultron</h1>
        <div className="users-scroll-container">
          <div className="users-row">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className="user-avatar-link skeleton">
                <div className="user-avatar-skeleton"></div>
                <div className="user-name-skeleton"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Match the exact CSS class and styling of the real filter button */}
        <div className="friend-profile__filter-container">
          <button className="friend-profile__filter-button skeleton-filter-button">
            <span>Alla</span>
          </button>
        </div>

        <div className="shows-list">
          {Array(6).fill(0).map((_, i) => (
            <ShowCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  const handleRatingClick = async (e, show) => {
    e.preventDefault();
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setSelectedShowForRating(show);
    setShowRatingOnlyModal(true);
  };

  const handleShare = async (e, show) => {
    e.preventDefault();
    if (!handleProtectedAction()) return;
    
    const shareData = {
      title: show.title,
      text: `Kolla in ${show.title} på Smultron!`,
      url: `${window.location.origin}/show/${show.id}`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setToast({ message: 'Länk kopierad!', type: 'success' });
      }

      // Add activity tracking for share with a value
      await addActivity(
        user.uid,
        'share',
        show.id,
        show.title,
        'shared'
      );
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        setToast({ message: 'Kunde inte dela', type: 'error' });
      }
    }
  };

  const handleRatingOverviewClick = async (e, show) => {
    e.preventDefault();
    e.stopPropagation();  // Add this to prevent show details navigation
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setSelectedShowForRating(show);
    setShowRatingsOverview(true);
    setLoadingRatings(true);

    try {
      const ratingsData = await getShowRatings(show.id);
      const ratingsWithUserDetails = await Promise.all(
        ratingsData.ratings.map(async (rating) => {
          const userDoc = await getDoc(doc(db, 'users', rating.userId));
          const userData = userDoc.data();
          let userName = 'Anonymous';
          if (userData?.displayName) {
            userName = userData.displayName;
          } else if (userData?.firstName || userData?.lastName) {
            userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          }
          return {
            ...rating,
            userName,
            userPhoto: userData?.photoURL
          };
        })
      );
      setShowRatings(ratingsWithUserDetails);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setToast({ message: 'Kunde inte hämta betyg', type: 'error' });
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleWatchlistClick = async (e, show) => {
    e.preventDefault();
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    setToast({ message: 'Sparad i din lista!', type: 'success' });
    
    try {
      await addToWatchlist(user.uid, show);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      setToast({ message: 'Något gick fel', type: 'error' });
    }
  };

  const handleRatingSubmit = async (rating, comment, show) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    setShowRatingModal(false);
    setToast({ message: 'Tack för din röst!', type: 'success' });

    try {
      const existingRating = await getUserRating(user.uid, show.id);
      if (existingRating) {
        await updateRating(user.uid, show.id, rating, comment);
      } else {
        await addRating(user.uid, show.id, rating, comment);
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setToast({ message: 'Något gick fel', type: 'error' });
    }
  };

  const handleRatingOnlySubmit = async (rating) => {
    if (!handleProtectedAction()) return;
    
    try {
      const { averageRating, ratingCount } = await addRating(
        user.uid, 
        selectedShowForRating.id, 
        rating
      );
      
      // Update local state with new values
      setShows(prevShows => 
        prevShows.map(show => 
          show.id === selectedShowForRating.id
            ? { ...show, averageRating, ratingCount }
            : show
        )
      );
      
      setShowRatingOnlyModal(false);
      setToast({ message: 'Betyg sparat!', type: 'success' });
      
      // Add activity tracking
      await addActivity(
        user.uid,
        'rating',
        selectedShowForRating.id,
        selectedShowForRating.title,
        rating
      );
    } catch (error) {
      console.error('Error submitting rating:', error);
      setToast({ message: 'Kunde inte spara betyg', type: 'error' });
    }
  };

  const handleStreamingSourceClick = async (e, show) => {
    e.preventDefault();
    setLoadingStreamingSources(true);
    
    try {
      // If we already have streaming sources in the show object, use them
      if (show.streamingSources && Array.isArray(show.streamingSources)) {
        setStreamingSources(prev => ({
          ...prev,
          [show.id]: show.streamingSources
        }));
      } else {
        // Otherwise fetch from database
        const showDoc = await getDoc(doc(db, 'shows', show.id.toString()));
        if (showDoc.exists()) {
          const showData = showDoc.data();
          setStreamingSources(prev => ({
            ...prev,
            [show.id]: showData.streamingSources || []
          }));
          
          // Update the show in our local state to have these streaming sources
          setShows(prevShows => 
            prevShows.map(s => 
              s.id === show.id
                ? { ...s, streamingSources: showData.streamingSources || [] }
                : s
            )
          );
        }
      }
      
      // Only open modal and set selected show after we have the data
      setSelectedShowForSources(show);
      setShowStreamingModal(true);
    } catch (error) {
      console.error('Error fetching streaming sources:', error);
      setToast({ message: 'Kunde inte hämta streamingtjänster', type: 'error' });
    } finally {
      setLoadingStreamingSources(false);
    }
  };

  const handleMarkAsSeen = async (e, show) => {
    e.preventDefault();
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      await markShowAsSeen(user.uid, show);
      setToast({ message: 'Markerad som sedd!', type: 'success' });
    } catch (error) {
      console.error('Error marking show as seen:', error);
      setToast({ message: 'Något gick fel', type: 'error' });
    }
  };

  const handleAddStreamingSource = async (source) => {
    if (!handleProtectedAction()) return;

    try {
      console.log('Starting to add streaming source:', source, 'for:', selectedShowForSources.title);
      
      // Show success toast immediately
      setToast({ message: 'Streamingtjänst tillagd!', type: 'success' });

      // Update local state immediately
      setStreamingSources(prev => ({
        ...prev,
        [selectedShowForSources.id]: [...(prev[selectedShowForSources.id] || []), source]
      }));

      // Update shows array immediately
      setShows(prevShows => prevShows.map(show => {
        if (show.id === selectedShowForSources.id) {
          return {
            ...show,
            streamingSources: [...(show.streamingSources || []), source]
          };
        }
        return show;
      }));

      // Update in database
      await updateDoc(doc(db, 'shows', selectedShowForSources.id.toString()), {
        streamingSources: arrayUnion(source),
        updatedAt: serverTimestamp()
      });

      console.log('Adding streaming activity...');
      await addActivity(
        user.uid, 
        'streaming', 
        selectedShowForSources.id, 
        selectedShowForSources.title, 
        source
      );
      console.log('Streaming activity added successfully');
      
    } catch (error) {
      console.error('Error in handleAddStreamingSource:', error);
      setToast({ message: 'Kunde inte lägga till streamingtjänst', type: 'error' });
    }
  };

  const handleRemoveStreamingSource = async (source) => {
    if (!handleProtectedAction()) return;

    try {
      // Show success toast immediately
      setToast({ message: 'Streamingtjänst borttagen', type: 'success' });

      // Update local state immediately
      setStreamingSources(prev => ({
        ...prev,
        [selectedShowForSources.id]: prev[selectedShowForSources.id].filter(s => s !== source)
      }));

      // Update shows array immediately
      setShows(prevShows => prevShows.map(show => {
        if (show.id === selectedShowForSources.id) {
          return {
            ...show,
            streamingSources: show.streamingSources?.filter(s => s !== source) || []
          };
        }
        return show;
      }));

      // Update in database
      await updateDoc(doc(db, 'shows', selectedShowForSources.id.toString()), {
        streamingSources: arrayRemove(source),
        updatedAt: serverTimestamp()
      });

      // Add activity tracking for removal
      await addActivity(
        user.uid, 
        'streaming_remove', 
        selectedShowForSources.id, 
        selectedShowForSources.title, 
        source
      );
    } catch (error) {
      console.error('Error removing streaming source:', error);
      // Show error toast and revert changes
      setToast({ message: 'Kunde inte ta bort streamingtjänst', type: 'error' });

      // Revert local state
      setStreamingSources(prev => ({
        ...prev,
        [selectedShowForSources.id]: [...(prev[selectedShowForSources.id] || []), source]
      }));

      // Revert shows array
      setShows(prevShows => prevShows.map(show => {
        if (show.id === selectedShowForSources.id) {
          return {
            ...show,
            streamingSources: [...(show.streamingSources || []), source]
          };
        }
        return show;
      }));
    }
  };

  // Add handler for filter changes
  const handleFilterChange = (newFilter) => {
    // Reset state to trigger a new data fetch
    setFilter(newFilter);
    setShows([]);
    setPage(1);
    setHasMore(true);
    setLastDoc(null);
    setLoading(true);
    setInitialLoad(true);
    
    // Update page state
    pageStateStore.setState('popular', { 
      ...pageStateStore.getState('popular'),
      filter: newFilter 
    });
  };

  const handleShowClick = (show) => {
    // Save current state
    pageStateStore.setState('popular', {
      scrollPosition: window.scrollY,
      filter
    });
    
    // Navigate to show details
    navigate(`/show/${show.id}`, {
      state: { from: 'popular' }
    });
  };

  const handleProtectedAction = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return false;
    }
    return true;
  };

  const handleStatusChange = async (show, newStatus) => {
    if (!handleProtectedAction()) return;

    try {
      console.log('Starting status change for:', show.title, 'to:', newStatus);
      
      // Create updated lists object
      const updatedLists = { ...userLists };
      
      // Remove show from all lists first
      ['watchingList', 'watchlist', 'seenList', 'archivedList'].forEach(listName => {
        if (updatedLists[listName]) {
          updatedLists[listName] = updatedLists[listName].filter(
            item => String(item.id) !== String(show.id)
          );
        }
      });

      // Add show to appropriate list
      switch (newStatus) {
        case SHOW_STATUSES.WATCHLIST:
          if (!updatedLists.watchlist) updatedLists.watchlist = [];
          updatedLists.watchlist.push(show);
          await addToWatchlist(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'watchlist');
          break;
        case SHOW_STATUSES.WATCHING:
          if (!updatedLists.watchingList) updatedLists.watchingList = [];
          updatedLists.watchingList.push(show);
          await markShowAsWatching(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'watching');
          break;
        case SHOW_STATUSES.SEEN:
          if (!updatedLists.seenList) updatedLists.seenList = [];
          updatedLists.seenList.push(show);
          await markShowAsSeen(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'seen');
          break;
        case SHOW_STATUSES.ARCHIVED:
          if (!updatedLists.archivedList) updatedLists.archivedList = [];
          updatedLists.archivedList.push(show);
          await archiveShow(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'archived');
          break;
      }

      // Update UI immediately
      setUserLists(updatedLists);
      
      setShowStatusMenu(false);
      setSelectedShowForStatus(null);
      setToast({ message: getStatusMessage(newStatus), type: 'success' });
    } catch (error) {
      console.error('Error in handleStatusChange:', error);
      setToast({ message: 'Kunde inte ändra status', type: 'error' });
    }
  };

  // Helper function to get status message
  const getStatusMessage = (status) => {
    switch (status) {
      case SHOW_STATUSES.WATCHING:
        return 'Tillagd i Tittar på!';
      case SHOW_STATUSES.WATCHLIST:
        return 'Tillagd i Ska se!';
      case SHOW_STATUSES.SEEN:
        return 'Markerad som sedd!';
      case SHOW_STATUSES.ARCHIVED:
        return 'Arkiverad!';
      default:
        return 'Status uppdaterad!';
    }
  };

  // Optimize the getShowStatus function - make it a regular function instead of useCallback to prevent dependency issues
  const getShowStatus = (show, lists) => {
    // Safety checks
    if (!lists || !show || !show.id) return SHOW_STATUSES.NONE;
    
    const { watchlist, seenList, watchingList, archivedList } = lists;
    
    // Convert show ID to string for comparison
    const showId = String(show.id);
    
    // Helper function to safely check if a show is in a list
    const isShowInList = (list, id) => {
      if (!Array.isArray(list)) return false;
      
      return list.some(item => {
        // Handle both object items and primitive items
        if (item && typeof item === 'object') {
          // Ensure we're comparing string IDs
          return String(item.id) === id;
        }
        return String(item) === id;
      });
    };
    
    // Check each list in order of priority
    if (isShowInList(watchingList, showId)) return SHOW_STATUSES.WATCHING;
    if (isShowInList(seenList, showId)) return SHOW_STATUSES.SEEN;
    if (isShowInList(watchlist, showId)) return SHOW_STATUSES.WATCHLIST;
    if (isShowInList(archivedList, showId)) return SHOW_STATUSES.ARCHIVED;
    
    return SHOW_STATUSES.NONE;
  };

  const handleStatusClick = (show, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const buttonRect = event.currentTarget.getBoundingClientRect();
    
    // Position menu to the left of the button by default
    let left = buttonRect.left - 220; // menu width (200px) + gap (20px)
    let top = buttonRect.top;
    
    // If not enough space on the left, position to the right
    if (left < 16) {
      left = buttonRect.right + 20;
    }
    
    // If not enough space on either side, position below
    if (left + 200 > window.innerWidth - 16) {
      left = buttonRect.left;
      top = buttonRect.bottom + 8;
    }
    
    setMenuPosition({ left, top });
    setSelectedShowForStatus(show);
    setCurrentShowStatus(getShowStatus(show, userLists));
    setShowStatusMenu(true);
  };

  const handleFilterToggle = (type) => {
    setFilter(prevFilter => {
      let newFilter;
      if (prevFilter.includes(type)) {
        // If this is the only active filter, don't remove it
        if (prevFilter.length === 1) {
          return prevFilter;
        }
        // Remove the type
        newFilter = prevFilter.filter(f => f !== type);
      } else {
        // Add the type
        newFilter = [...prevFilter, type];
      }
      
      // Update page state store
      pageStateStore.setState('popular', {
        ...pageStateStore.getState('popular'),
        filter: newFilter
      });
      
      return newFilter;
    });
  };

  return (
    <div className="popular-container">
      <h1>Smultron</h1>
      
      <div className="users-scroll-container">
        <div className="users-row">
          {loadingUsers ? (
            Array(8).fill(0).map((_, i) => (
              <div key={i} className="user-avatar-link skeleton">
                <div className="user-avatar-skeleton"></div>
                <div className="user-name-skeleton"></div>
              </div>
            ))
          ) : allUsers.length > 0 ? (
            allUsers.map((userData) => {
              // Safely access activities
              const activities = userActivities[userData.uid] || [];
              const lastViewed = lastViewedTimestamps[userData.uid];
              
              const hasUnreadActivity = userData.uid !== user?.uid && 
                activities.length > 0 && 
                activities.some(activity => {
                  return !lastViewed || activity.timestamp > lastViewed;
                });

              return (
                <Link 
                  key={userData.uid}
                  to={`/friend/${userData.uid}?hasUnseenActivity=${hasUnreadActivity}`}
                  className="user-avatar-link"
                >
                  <UserAvatar 
                    user={userData}
                    size="medium"
                    hasUnreadActivity={hasUnreadActivity || false}
                  />
                  <span className="user-name">
                    {userData.uid === user?.uid ? 'Mina' : userData.displayName?.split(' ')[0]}
                  </span>
                </Link>
              );
            })
          ) : (
            <div className="no-users-message">
              Inga användare hittades
            </div>
          )}
        </div>
      </div>

      <CustomSelect
        value={filter}
        onChange={handleFilterChange}
      />

      <div className="shows-list">
        {filteredShows.map((show, index) => {
          const showStatus = listsLoading 
            ? 'none' 
            : getShowStatus(show, userLists);
          
          return (
            <ShowCard
              key={`${show.id}-${index}`}
              show={{
                ...show,
                rating: undefined,
                averageRating: show.averageRating,
                ratingCount: show.ratingCount,
                noRatingText: "Inga röster än",
                streamingSources: show.streamingSources || []
              }}
              index={index}
              showStatus={showStatus}
              isLoadingStatus={listsLoading}
              onStatusClick={(e) => handleStatusClick(show, e)}
              onRatingClick={(e) => handleRatingClick(e, show)}
              onWatchlistClick={(e) => handleWatchlistClick(e, show)}
              onShareClick={(e) => handleShare(e, show)}
              onStreamingSourceClick={(e) => handleStreamingSourceClick(e, show)}
              onRatingOverviewClick={(e) => handleRatingOverviewClick(e, show)}
              onMarkAsSeen={(e) => handleMarkAsSeen(e, show)}
              onClick={() => handleShowClick(show)}
            />
          );
        })}

        {showStatusMenu && selectedShowForStatus && (
          <StatusMenu
            show={selectedShowForStatus}
            position={menuPosition}
            onClose={() => {
              setShowStatusMenu(false);
              setSelectedShowForStatus(null);
            }}
            currentStatus={currentShowStatus}
            onStatusChange={handleStatusChange}
          />
        )}

        {/* Loading indicator for infinite scroll */}
        <div ref={loadMoreRef} className="loading-more-trigger">
          {loadingMore && <Spinner size="small" />}
        </div>
      </div>
      
      {showStreamingModal && (
        <StreamingSourceModal
          show={selectedShowForSources}
          onClose={() => setShowStreamingModal(false)}
          existingSources={streamingSources[selectedShowForSources?.id] || []}
          isLoading={loadingStreamingSources}
          onAddSource={handleAddStreamingSource}
          onRemoveSource={handleRemoveStreamingSource}
        />
      )}

      {showRatingsOverview && (
        <RatingsOverviewModal
          show={selectedShowForRating}
          ratings={showRatings}
          isLoading={loadingRatings}
          onClose={() => {
            setShowRatingsOverview(false);
            setShowRatings([]);
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {showRatingModal && (
        <RatingModal
          show={selectedShow}
          onClose={() => setShowRatingModal(false)}
          onSubmit={handleRatingSubmit}
        />
      )}
      
      {showRatingOnlyModal && (
        <RatingOnlyModal
          show={selectedShowForRating}
          onClose={() => setShowRatingOnlyModal(false)}
          onSubmit={(rating) => handleRatingOnlySubmit(rating)}
        />
      )}

      {showLoginPrompt && (
        <LoginPrompt 
          onClose={() => setShowLoginPrompt(false)}
          message="Du måste vara inloggad för att göra detta."
        />
      )}
    </div>
  );
}

export default Popular; 